import type { BrowserClient, BrowserConfig, Plugin } from '@amplitude/analytics-types';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { amplitudeTrack, initAnalytics, type DatadogInitModel } from '@cw-elements/analytics-provider';
import { isTrustedDomain } from '@protector/api-client/config/domain';
import { VITE_AMPLITUDE_ID } from 'config/constants';
import { getSystemFlags } from 'core/core.selectors';
import useVersion from 'core/shared/version/useVersion.hook';
import { getCurrentLanguage } from 'core/utils/functions';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

interface DatadogTraceConfig {
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  telemetrySampleRate: number;
  traceSampleRate: number;
  trackResources: boolean;
  trackLongTasks: boolean;
  trackUserInteractions: boolean;
  enablePrivacyForActionName: boolean;
  compressIntakeRequests: boolean;
}

interface ExtendedGlobalThis {
  amplitude?: BrowserClient;
  trackEvent?: (eventName: string, properties?: Record<string, unknown>) => void;
}

const DEFAULT_TRACE_CONFIG: DatadogTraceConfig = {
  sessionReplaySampleRate: 100,
  sessionSampleRate: 100,
  telemetrySampleRate: 100,
  traceSampleRate: 100,
  trackResources: true,
  trackLongTasks: true,
  trackUserInteractions: true,
  enablePrivacyForActionName: false,
  compressIntakeRequests: true
};

const resolveTraceConfig = (rawOverride: string | undefined): DatadogTraceConfig => {
  if (!rawOverride) {
    return DEFAULT_TRACE_CONFIG;
  }

  try {
    return { ...DEFAULT_TRACE_CONFIG, ...(JSON.parse(rawOverride) as Partial<DatadogTraceConfig>) };
  } catch {
    return DEFAULT_TRACE_CONFIG;
  }
};

const buildDatadogConfig = (version: string, traceConfig: DatadogTraceConfig): DatadogInitModel => ({
  applicationId: '74c15d9c-1280-45ab-af92-b011d9e94a1e',
  clientToken: 'pub9de9f41aa1baae5fcdebe8e657d17812',
  site: 'datadoghq.eu',
  version: version,
  service: 'web-react-protector-user-client',
  env: process.env.VITE_ENV || 'unknown',
  defaultPrivacyLevel: 'mask-user-input',
  silentMultipleInit: true,
  beforeSend: event => {
    if (event.type === 'resource' && !isTrustedDomain(event.resource.url)) {
      return false;
    }

    return true;
  },
  ...traceConfig
});

const useInitAnalytics = () => {
  const systemFlags = useSelector(getSystemFlags);
  const version = useVersion();
  const initialized = useRef(false);

  const tracingEnabled = useMemo(() => systemFlags?.P40_44838_DATADOG_RUM, [systemFlags?.P40_44838_DATADOG_RUM]);

  const traceConfig = useMemo<DatadogTraceConfig>(
    () => resolveTraceConfig(systemFlags?.P40_44838_DATADOG_CONFIG as string | undefined),
    [systemFlags?.P40_44838_DATADOG_CONFIG]
  );

  useEffect(() => {
    if (initialized.current || !systemFlags || !VITE_AMPLITUDE_ID) {
      return;
    }

    if (tracingEnabled && version === undefined) {
      return;
    }
    initialized.current = true;

    const locale = getCurrentLanguage()?.split('-')[0] ?? 'en';
    const datadog = tracingEnabled && version ? buildDatadogConfig(version, traceConfig) : undefined;

    void initAnalytics({
      amplitude: { clientId: VITE_AMPLITUDE_ID },
      datadog: datadog,
      consent: { enabled: true }
    }).then(client => {
      if (!client) {
        return;
      }

      client.add(engagementPlugin({ locale: locale }) as Plugin<BrowserClient, BrowserConfig>);

      const globalExtended = globalThis as unknown as typeof globalThis & ExtendedGlobalThis;
      globalExtended.amplitude = client;
      globalExtended.trackEvent = amplitudeTrack;
    });
  }, [systemFlags, version, tracingEnabled, traceConfig]);
};

export default useInitAnalytics;
